"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Star } from "lucide-react"
import { useSelector } from "react-redux"
import type { RootState } from "../../store/store"
import { productService } from "../../services/productService"
import toast from "react-hot-toast"

interface RatingProps {
  productId: string
  averageRating?: number // Made optional with default
  totalRatings?: number // Made optional with default
  onRatingChange?: (newRating: number, newTotal: number) => void
  showInteractive?: boolean
}

const Rating: React.FC<RatingProps> = ({
  productId,
  averageRating = 0, // Default value
  totalRatings = 0, // Default value
  onRatingChange,
  showInteractive = true,
}) => {
  const [userRating, setUserRating] = useState<number | null>(null)
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [reviewText, setReviewText] = useState("")
  const { user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    const fetchUserRating = async () => {
      if (user) {
        try {
          const rating = await productService.getUserRating(productId, user.uid)
          if (rating) {
            setUserRating(rating.rating)
            setReviewText(rating.review || "")
          }
        } catch (error) {
          console.error("Error fetching user rating:", error)
        }
      }
    }

    if (productId) { // Only fetch if productId exists
      fetchUserRating()
    }
  }, [productId, user])

  const handleRatingClick = async (rating: number) => {
    if (!user) {
      toast.error("Please sign in to rate products")
      return
    }

    if (!showInteractive) return

    setIsSubmitting(true)
    try {
      await productService.rateProduct(productId, user.uid, rating, reviewText)
      setUserRating(rating)
      setShowReviewForm(false)

      // Fetch updated product data to get new averages
      const updatedProduct = await productService.getProductById(productId)
      if (updatedProduct && onRatingChange) {
        onRatingChange(updatedProduct.averageRating, updatedProduct.totalRatings)
      }

      toast.success("Rating submitted successfully!")
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error("Failed to submit rating")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStarClick = (rating: number) => {
    if (!user || !showInteractive) return

    if (userRating === rating) {
      // If clicking the same rating, show review form
      setShowReviewForm(true)
    } else {
      // If clicking a different rating, update immediately
      handleRatingClick(rating)
    }
  }

  const handleReviewSubmit = () => {
    if (userRating) {
      handleRatingClick(userRating)
    }
  }

  const renderStars = (interactive = false) => {
    return [1, 2, 3, 4, 5].map((star) => {
      const rating = interactive ? hoverRating || userRating || 0 : averageRating || 0
      const filled = star <= rating
      const isPartiallyFilled = !filled && star - 1 < rating && rating < star

      return (
        <button
          key={star}
          type="button"
          className={`relative transition-colors ${
            interactive && showInteractive
              ? isSubmitting
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer hover:scale-110"
              : "cursor-default"
          }`}
          onClick={() => interactive && handleStarClick(star)}
          onMouseEnter={() => interactive && !isSubmitting && setHoverRating(star)}
          onMouseLeave={() => interactive && !isSubmitting && setHoverRating(null)}
          disabled={isSubmitting}
        >
          <Star
            className={`w-5 h-5 transition-colors ${
              filled ? "text-yellow-400 fill-yellow-400" : isPartiallyFilled ? "text-yellow-400" : "text-gray-300"
            }`}
          />
          {isPartiallyFilled && (
            <Star
              className="absolute top-0 left-0 w-5 h-5 text-yellow-400 fill-yellow-400"
              style={{
                clipPath: `inset(0 ${100 - (rating - (star - 1)) * 100}% 0 0)`,
              }}
            />
          )}
        </button>
      )
    })
  }

  // Don't render if no productId
  if (!productId) {
    return null
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
        {/* Display Rating */}
        <div className="flex items-center space-x-2">
          <div className="flex items-center">{renderStars(false)}</div>
          <span className="text-sm text-gray-600">
            {(averageRating || 0).toFixed(1)} ({totalRatings || 0} {totalRatings === 1 ? "rating" : "ratings"})
          </span>
        </div>

        {/* Interactive Rating for logged-in users */}
        {user && showInteractive && (
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Your rating:</span>
              <div className="flex items-center">{renderStars(true)}</div>
              {userRating && (
                <span className="text-sm text-primary-600 font-medium">
                  {userRating} star{userRating !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {showReviewForm && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Write a review (optional)..."
                  className="w-full p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleReviewSubmit}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Review"}
                  </button>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!user && showInteractive && (
        <p className="text-sm text-gray-500">
          <span className="text-primary-600 cursor-pointer hover:underline">Sign in</span> to rate this product
        </p>
      )}
    </div>
  )
}

export default Rating